import { idioma, tx } from '../i18n';
import { vivo } from '../i18n/vivo';
import { desde } from './formato';
import type { ApplianceFila, ErrorSalud, Rol } from '../tipos';

/** Cómo se pinta un estado: el color lo dice antes que el texto. */
export type Tono = 'arriba' | 'parcial' | 'caido' | 'desconocido';

/**
 * Por dónde se entra a la app de un cliente (decisión 19). En producción la dirección la
 * dice `VITE_PUERTA`; en desarrollo se arma con **el mismo nombre de host del panel** y el
 * puerto de la puerta: si el panel está en `localhost` y la puerta en `127.0.0.1`, para el
 * navegador son sitios distintos y la galleta de la sesión no viaja (el NOC vería
 * "Sign in to the Control Center first").
 */
/**
 * Por dónde se entra a la app de un cliente. Lo dice **la API** (viene con la sesión): estaba
 * horneada al compilar y en producción apuntaba a un puerto que no existe, así que el marco
 * quedaba en blanco. Lo de acá es solo el respaldo para desarrollo.
 */
export let PUERTA = import.meta.env.VITE_PUERTA ?? `${location.protocol}//${location.hostname}:3101`;

export function ponerPuerta(url: string | undefined) {
  if (url) PUERTA = url;
}

export interface EstadoVivo {
  texto: string;
  tono: Tono;
  /** Para ordenar la lista: lo que urge, arriba. */
  peso: number;
}

/**
 * El estado que se ve en la lista. No es el campo `estado` a secas: un appliance activo
 * que hace rato no habla está "sin contacto", que es lo que a uno le importa de un vistazo.
 */
export function estadoVivo(a: ApplianceFila): EstadoVivo {
  if (a.estado === 'revocado') return { texto: tx.appliances.revocado, tono: 'desconocido', peso: 1 };
  if (a.estado === 'suspendido') return { texto: tx.appliances.suspendido, tono: 'parcial', peso: 3 };
  if (a.estado === 'alta') return { texto: tx.estado.esperandoAlta, tono: 'desconocido', peso: 2 };
  if (a.en_linea) return { texto: tx.estado.enLinea, tono: 'arriba', peso: 0 };
  return { texto: a.visto ? tx.estado.sinContacto(desde(a.visto)) : tx.estado.nuncaConecto, tono: 'caido', peso: 4 };
}

/** Por qué un appliance necesita que alguien lo mire. Vacío = está bien. */
export function motivosDeAtencion(a: ApplianceFila): string[] {
  const m: string[] = [];
  if (a.estado === 'activo' && !a.en_linea) m.push(a.visto ? tx.estado.sinContacto(desde(a.visto)) : tx.estado.nuncaConecto);
  if (a.disco_pc !== null && a.disco_pc > 85) m.push(tx.estado.discoAl(Math.round(a.disco_pc)));
  if (a.motores > a.motores_conectados) m.push(tx.estado.motoresCaidos(a.motores - a.motores_conectados, a.motores));
  return m;
}

/** El disco en rojo pasado el 85 %, en ámbar desde el 70 %: los mismos cortes del aviso. */
export function tonoDisco(v: number | null | undefined): Tono {
  if (v === null || v === undefined) return 'desconocido';
  if (v > 85) return 'caido';
  if (v > 70) return 'parcial';
  return 'arriba';
}

/** Lo que dice la licencia del appliance, con el color que le corresponde. */
export function tonoLicencia(estado: string | undefined): Tono {
  if (estado === 'vigente') return 'arriba';
  if (estado === 'por_vencer' || estado === 'gracia') return 'parcial';
  if (estado === 'solo_lectura') return 'caido';
  return 'desconocido';
}

/** Los estados de licencia del protocolo, dichos para la persona. `vivo`: siguen al idioma. */
export const LICENCIA: Record<string, string> = vivo(() => tx.licencia.estados);

/** Los dos canales de versiones, dichos para la persona (el valor que viaja no cambia). */
export const CANAL: Record<string, string> = vivo(() => tx.comun.canales);

/**
 * Un texto que mandó el appliance (un error del sistema, cómo terminó una orden). Viene en
 * los dos idiomas: se muestra el del panel, no siempre el inglés.
 */
export const bilingue = (d: { en?: string; es?: string } | null | undefined): string | undefined =>
  (idioma() === 'es' ? (d?.es ?? d?.en) : (d?.en ?? d?.es)) || undefined;

export const textoError = (e: ErrorSalud) => bilingue(e.detalle) ?? e.que;

/**
 * Qué puede hacer cada rol (la API manda igual: esto es para no ofrecer botones que van a
 * dar 403). `ventas` crea organizaciones y appliances, pero no toca el canal de ninguno.
 */
export const permisos = (rol: Rol | undefined) => ({
  crearOrganizacion: rol === 'admin' || rol === 'ventas',
  editarOrganizacion: rol === 'admin' || rol === 'ventas',
  crearAppliance: rol === 'admin' || rol === 'ventas',
  /** Pedir un código nuevo es parte de dar de alta: ventas también. */
  pedirCodigo: rol === 'admin' || rol === 'ventas',
  editarAppliance: rol === 'admin',
  suspender: rol === 'admin' || rol === 'soporte',
  revocar: rol === 'admin',
  borrar: rol === 'admin',
  verBitacora: rol === 'admin' || rol === 'soporte',
  /** La gente del panel: solo admin. */
  manejarUsuarios: rol === 'admin',
  /** Las claves de API (con qué entra un sistema de un tercero): solo admin. */
  manejarClaves: rol === 'admin',
  /** Entrar a la app del cliente (decisión 19): quien opera, no quien vende. */
  abrirRed: rol === 'admin' || rol === 'soporte',
  /** Emitir y renovar licencias es lo que se vende: ventas, además de admin. */
  emitirLicencia: rol === 'admin' || rol === 'ventas',
  revocarLicencia: rol === 'admin',
  /** Publicar una versión es para todo el parque: solo admin. */
  publicarVersion: rol === 'admin',
  /** Pedirle a un appliance que se actualice lo hace quien atiende. */
  actualizar: rol === 'admin' || rol === 'soporte',
});

